import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import CreateVoiceDialog from '../components/voice/CreateVoiceDialog';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { Search, ChevronDown } from 'lucide-react';
import { CreateCustomProfileModal, SetDefaultVoiceModal } from '../components/ai-voices/ConfigureVoiceModal';
import { VoicesV2 } from '../components/ai-voices/VoicesV2';
import { MyVoices } from '../components/ai-voices/MyVoices';
import { BookmarkedVoicesList } from '../components/ai-voices/BookmarkedVoicesList';
import { TrendingVoices } from '../components/ai-voices/TrendingVoices';
import { LanguageCollections } from '../components/ai-voices/LanguageCollections';
import AIVoicesSkeleton from '../components/skeletons/pages/AIVoicesSkeleton';
import useIsMobile from '../hooks/useIsMobile';
import { useLanguages } from '../http-hooks/ai-voices/useLanguages';
import { useOrganisation } from '../http-hooks/organisation/useOrganisation';
import { useAIVoicesStore } from '../zustand-store/useAIVoicesStore';
import { useIntegration } from '../http-hooks/integration/useIntegration';
import { isDeprecatedAudioProfile } from '../constants/deprecatedVoices';
import { DEFAULT_LANG_CODE } from '../constants/constants';
import { C } from '../styles/tokens';
import { Ico } from '../components/ui/Icons';
import { languageNameFromCode } from '../utils/languageDisplay';
import { SearchableSelect } from '../components/ui/SearchableSelect';
import { langPrefix, buildLanguageGroups, buildLanguageOptions, buildAccentOptions, resolveGroupPrefix } from '../utils/languageGrouping';

// ── Static filter options ───────────────────────────────────────────────────

const GENDER_FILTER_OPTIONS = [
    { value: 'all', label: 'All Genders' },
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
];

const QUALITY_FILTER_OPTIONS = [
    { value: 'all', label: 'All Qualities' },
    { value: 'emotional', label: 'Emotional' },
    { value: 'neural', label: 'Premium' },
    { value: 'standard', label: 'Standard' },
];

// ── VoicesBanner ───────────────────────────────────────────────────────────

const VOICES_BANNER_DISMISSED_KEY = 'voicesBannerDismissed';

function VoicesBanner() {
    // Persist dismissal locally so the banner stays hidden across reloads.
    const [visible, setVisible] = useState<boolean>(() => {
        try { return localStorage.getItem(VOICES_BANNER_DISMISSED_KEY) !== 'true'; }
        catch { return true; }
    });

    const dismiss = () => {
        setVisible(false);
        try { localStorage.setItem(VOICES_BANNER_DISMISSED_KEY, 'true'); }
        catch { /* ignore storage errors (e.g. private mode) */ }
    };

    if (!visible) return null;
    return (
        <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 14px',
            background: '#FFFBEB',
            border: '1px solid #FDE68A',
            borderRadius: 9,
            fontSize: 13,
            color: C.n700,
            marginBottom: 20,
        }}>
            <span style={{ flex: 1 }}>
                <strong>900+ premium voices</strong> across 140+ languages — all with full commercial rights and emotional range.
            </span>
            <button
                onClick={dismiss}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex', flexShrink: 0 }}
            >
                <Ico n="x" size={14} color={C.n400} />
            </button>
        </div>
    );
}

// ── Create-voice widget ──────────────────────────────────────────────────────
// Clone navigates to its own page; Design/Remix open CreateVoiceDialog as a modal/sheet.

type VoiceCreateMode = 'clone' | 'design' | 'remix';

const CREATE_VOICE_MODES: {
    id: VoiceCreateMode;
    title: string;
    desc: string;
    icon: React.ReactNode;
    accent: string;
    tint: string;
}[] = [
    {
        id: 'clone', title: 'Voice clone', desc: 'Clone your voice from a short sample',
        icon: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" />
                <path d="M19 10a7 7 0 0 1-14 0M12 19v3M8 22h8" />
            </svg>
        ),
        accent: C.p600, tint: C.p50,
    },
    {
        id: 'design', title: 'Voice design', desc: 'Describe a voice, we generate it',
        icon: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 3l1.9 5.8L20 10l-6.1 1.2L12 17l-1.9-5.8L4 10l6.1-1.2z" />
            </svg>
        ),
        accent: '#7C3AED', tint: '#F5F3FF',
    },
    {
        id: 'remix', title: 'Voice remix', desc: 'Blend two voices into a new one',
        icon: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="16 3 21 3 21 8" />
                <line x1="4" y1="20" x2="21" y2="3" />
                <polyline points="21 16 21 21 16 21" />
                <line x1="15" y1="15" x2="21" y2="21" />
                <line x1="4" y1="4" x2="9" y2="9" />
            </svg>
        ),
        accent: '#DC2626', tint: '#FEF2F2',
    },
];

type CreateVoiceMode = typeof CREATE_VOICE_MODES[number];


function ModeButton({ m, onClick }: { m: CreateVoiceMode; onClick: () => void }) {
    return (
        <button
            onClick={onClick}
            style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '13px 14px',
                width: '100%',
                background: C.w,
                border: `1.5px solid ${C.n200}`,
                borderRadius: 12, cursor: 'pointer', textAlign: 'left',
                transition: 'border-color .12s, background .12s',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = m.accent; e.currentTarget.style.background = m.tint; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = C.n200; e.currentTarget.style.background = C.w; }}
        >
            <span style={{
                width: 38, height: 38, borderRadius: 10, background: m.tint, color: m.accent,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
                {m.icon}
            </span>
            <span style={{ minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 14, fontWeight: 700, color: C.n900 }}>{m.title}</span>
                <span style={{ display: 'block', fontSize: 11.5, color: C.n500, marginTop: 1 }}>{m.desc}</span>
            </span>
            <span style={{ marginLeft: 'auto', flexShrink: 0, display: 'flex' }}>
                <ChevronDown size={14} color={C.n400} style={{ transform: 'rotate(-90deg)' }} />
            </span>
        </button>
    );
}

function CreateVoiceWidget({ onSelectMode, isMobile }: {
    onSelectMode: (m: VoiceCreateMode) => void;
    isMobile: boolean;
}) {
    return (
        <div style={{
            background: C.w, border: `1px solid ${C.n200}`, borderRadius: 16,
            padding: isMobile ? '14px 14px 16px' : '18px 20px 20px', marginBottom: 20,
            boxShadow: '0 1px 3px rgba(15,23,42,.04)',
        }}>
            <div style={{
                display: isMobile ? 'flex' : 'grid',
                flexDirection: isMobile ? 'column' : undefined,
                gridTemplateColumns: isMobile ? undefined : 'repeat(3, 1fr)',
                gap: 10,
            }}>
                {CREATE_VOICE_MODES.map(m => (
                    <ModeButton key={m.id} m={m} onClick={() => onSelectMode(m.id)} />
                ))}
            </div>
        </div>
    );
}

// ── Tabs config ────────────────────────────────────────────────────────────

type TabType = 'explore' | 'my-voices' | 'favourites';

const TABS: { id: TabType; label: string; sparkle?: boolean }[] = [
    { id: 'explore', label: 'Explore', sparkle: true },
    { id: 'my-voices', label: 'My Voices' },
    { id: 'favourites', label: 'Bookmarks' },
];

// ── Main page ──────────────────────────────────────────────────────────────

export default function AIVoicesV2() {
    const { org_id, id } = useParams<{ org_id: string; id: string }>();
    const history = useHistory();
    const location = useLocation();

    const {
        setLanguages, languages, selectedLanguage, setSelectedLanguage,
        filters, resetFilters, setGender, setQuality, setStyle,
    } = useAIVoicesStore(state => state);

    const { data: integration } = useIntegration({ org_id, integration_id: id });
    const legacy = isDeprecatedAudioProfile(integration?.audio_profile);
    const { isLoading } = useLanguages((data) => setLanguages(data), { legacy });

    const { data: organisation } = useOrganisation({ org_id });
    const bookmarkedCount = Object.keys(organisation?.bookmarked_voices ?? {}).length;

 
    const defaultAudioProfile = integration?.audio_profile;
    const defaultVoice = defaultAudioProfile?.lang_code
        ? languages[defaultAudioProfile.lang_code]?.voices.find(
            v => v.code === defaultAudioProfile.voice && v.engine === defaultAudioProfile.engine
        )
        : undefined;

    const [activeTab, setActiveTab] = useState<TabType>('explore');
    const [searchQuery, setSearchQuery] = useState('');
    // Which mode's dialog (design/remix) is currently open — clone never sets this, it navigates away instead.
    const [openModal, setOpenModal] = useState<'design' | 'remix' | null>(null);
    const [filteredCount, setFilteredCount] = useState(0);
    const isMobile = useIsMobile();
    const handleVoicesCountChange = useCallback((count: number) => setFilteredCount(count), []);
    const myVoicesRef = useRef<HTMLDivElement>(null);

    // ── Local language / accent state ──────────────────────────────────────

    // Initialise from whatever the store already has (usually 'en-US' from setLanguages)
    const [selectedLangPrefix, setSelectedLangPrefix] = useState<string>(() =>
        langPrefix(selectedLanguage?.code || 'en')
    );
    const [selectedLangCode, setSelectedLangCode] = useState<string>(() =>
        selectedLanguage?.code || ''
    );

    // ── Language groups (prefix → list of locale objects) ─────────────────

    const langGroups = useMemo(() => buildLanguageGroups(languages), [languages]);

    // ── Sorted language options ────────────────────────────────────────────

    const languageOptions = useMemo(() => buildLanguageOptions(langGroups), [langGroups]);

    // ── Accent options for the selected language prefix ────────────────────

    const accentOptions = useMemo(() => {
        const sorted = buildAccentOptions(langGroups, selectedLangPrefix);
        if (sorted.length === 0) return [];
        return [{ value: '', label: 'All accents' }, ...sorted];
    }, [selectedLangPrefix, langGroups]);

    const showAccentPicker = accentOptions.length > 0;

    // ── Helpers to push a language selection into the store ────────────────

    const applyLangToStore = (prefix: string, langCode: string) => {
        if (!prefix || !langGroups[prefix]) return;
        if (langCode && languages[langCode]) {
            // Specific accent selected
            setSelectedLanguage(languages[langCode]);
        } else {
            // No accent → combine all voices from every locale of this language
            const combinedVoices = langGroups[prefix].flatMap(l => l.voices || []);
            const first = langGroups[prefix][0];
            setSelectedLanguage({
                ...first,
                code: prefix,
                name: languageNameFromCode(prefix),
                voices: combinedVoices,
            });
        }
    };

    const handleLangChange = (prefix: string) => {
        setSelectedLangPrefix(prefix);
        setSelectedLangCode('');
        applyLangToStore(prefix, '');
    };

    const handleAccentChange = (langCode: string) => {
        setSelectedLangCode(langCode);
        applyLangToStore(selectedLangPrefix, langCode);
    };

    // Sync local prefix/code once languages load, or if the stored code's raw prefix isn't a valid group key (merged locales).
    useEffect(() => {
        if (selectedLanguage && Object.keys(langGroups).length > 0 && !langGroups[selectedLangPrefix]) {
            const prefix = resolveGroupPrefix(langGroups, selectedLanguage.code);
            setSelectedLangPrefix(prefix);
            setSelectedLangCode(selectedLanguage.code);
        }
    }, [selectedLanguage, langGroups]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Tab routing ────────────────────────────────────────────────────────

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const tabParam = params.get('tab');
        if (tabParam === 'my-voices' || tabParam === 'favourites') {
            setActiveTab(tabParam as TabType);
        } else if (!tabParam) {
            setActiveTab('explore');
        }
    }, [location.search]);

    const handleTabChange = (tab: TabType) => {
        setActiveTab(tab);
        setSearchQuery('');
        const baseUrl = `/organisation/${org_id}/integration/${id}/voice`;
        history.push(tab === 'explore' ? baseUrl : `${baseUrl}?tab=${tab}`);
    };

    // Fires as soon as the design/remix record is created (not once it's actually 'ready') —
    // close the dialog and jump to My Voices immediately; the list itself polls and shows a
    // loading card for the voice until its status flips to 'ready'.
    const handleVoiceCreated = () => {
        setOpenModal(null);
        handleTabChange('my-voices');
        setTimeout(() => {
            myVoicesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 150);
    };

    // Clone always goes to its own dedicated page; design/remix open as a dialog.
    const handleSelectMode = (m: VoiceCreateMode) => {
        if (m === 'clone') {
            history.push(`/organisation/${org_id}/integration/${id}/voice/instant-voice-clone`);
        } else {
            setOpenModal(m);
        }
    };

    // ── Active filter count (for Reset button) ─────────────────────────────

    const hasActiveFilters = filters.gender !== 'all' || filters.quality !== 'all' || filters.style;

    // Trending voices & curated collections are an English-default showcase that
    // does not respond to filters (PD-789). Hide both as soon as the user changes
    // any filter from its original state (language/accent/gender/quality/style),
    // or starts searching for a specific voice.
    const isDefaultLanguageSelection =
        selectedLangPrefix === langPrefix(DEFAULT_LANG_CODE) &&
        (!selectedLangCode || selectedLangCode === DEFAULT_LANG_CODE);
    const filtersChanged = !isDefaultLanguageSelection || hasActiveFilters || !!searchQuery.trim();

    // ── Render ─────────────────────────────────────────────────────────────

    return (
        <div>

            {/* ── Title row ── */}
            <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                marginBottom: 20, gap: 12, flexWrap: 'wrap',
            }}>
                <h1 style={{ fontSize: 20, fontWeight: 700, color: C.n900, margin: 0 }}>Voices</h1>

                {defaultAudioProfile && (
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        fontSize: 12.5, color: C.n700, background: C.n50,
                        border: `1px solid ${C.n200}`, borderRadius: 999,
                        padding: '5px 12px', whiteSpace: 'nowrap',
                    }}>
                        <span aria-hidden>⭐</span>
                        <span style={{ color: C.n500 }}>Default voice:</span>
                        <span style={{ fontWeight: 700 }}>
                            {defaultVoice
                                ? `${defaultVoice.name} (${defaultVoice.gender.charAt(0).toUpperCase()})`
                                : defaultAudioProfile.voice}
                        </span>
                    </div>
                )}
            </div>

            {/* ── Create voice widget ── */}
            <CreateVoiceWidget onSelectMode={handleSelectMode} isMobile={isMobile} />

            <CreateVoiceDialog
                isOpen={openModal !== null}
                initialOption={openModal}
                lockOption
                onClose={() => setOpenModal(null)}
                onSuccess={handleVoiceCreated}
                org_id={org_id}
                integration_id={id}
            />

            {/* ── Tabs ── */}
            <div style={{
                display: 'flex', alignItems: 'center',
                borderBottom: `1px solid ${C.n200}`,
                marginBottom: 16,
            }}>
                {TABS.map(t => (
                    <button
                        key={t.id}
                        onClick={() => handleTabChange(t.id)}
                        style={{
                            padding: '10px 14px',
                            border: 'none',
                            borderBottom: `2px solid ${activeTab === t.id ? C.n900 : 'transparent'}`,
                            marginBottom: -1,
                            background: 'transparent',
                            fontSize: 14,
                            fontWeight: activeTab === t.id ? 700 : 500,
                            color: activeTab === t.id ? C.n900 : C.n400,
                            cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: 5,
                            transition: 'color .1s',
                        }}
                    >
                        {t.sparkle && <span style={{ fontSize: 12 }}>✦</span>}
                        {t.label}
                        {t.id === 'favourites' && bookmarkedCount > 0 && (
                            <span style={{
                                background: C.n900, color: C.w, borderRadius: 999,
                                fontSize: 9.5, fontWeight: 700, padding: '1px 6px', marginLeft: 2,
                            }}>
                                {bookmarkedCount}
                            </span>
                        )}
                    </button>
                ))}
            </div>

            {/* ── Search ── */}
            <div style={{ position: 'relative', marginBottom: 8 }}>
                <Search
                    size={15}
                    style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: C.n400, pointerEvents: 'none' }}
                />
                <input
                    type="text"
                    placeholder={
                        activeTab === 'explore' ? 'Search library voices…' :
                            activeTab === 'my-voices' ? 'Search my voices…' :
                                'Search profiles…'
                    }
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    style={{
                        width: '100%', paddingLeft: 36, paddingRight: 12,
                        height: 38, border: `1px solid ${C.n200}`, borderRadius: 8,
                        fontSize: 13, color: C.n900, background: C.w,
                        outline: 'none', boxSizing: 'border-box',
                    }}
                />
            </div>

            {/* ── Inline filters (explore tab only) ── */}
            {activeTab === 'explore' && (
                <div style={{
                    display: 'flex', gap: 8, marginBottom: 24,
                    flexWrap: 'wrap', alignItems: 'center',
                }}>

                    {/* Language */}
                    <div style={{ width: 200 }}>
                        <SearchableSelect
                            selected={selectedLangPrefix}
                            onChange={handleLangChange}
                            options={languageOptions}
                            placeholder="Language"
                        />
                    </div>

                    {/* Accent (only when selected language has multiple locales) */}
                    {showAccentPicker && (
                        <div style={{ width: 180 }}>
                            <SearchableSelect
                                selected={selectedLangCode}
                                onChange={handleAccentChange}
                                options={accentOptions}
                                placeholder="All accents"
                            />
                        </div>
                    )}

                    {/* Gender */}
                    <div style={{ width: 150 }}>
                        <SearchableSelect
                            selected={filters.gender}
                            onChange={(v: string) => setGender(v)}
                            options={GENDER_FILTER_OPTIONS}
                            searchable={false}
                        />
                    </div>

                    {/* Quality */}
                    <div style={{ width: 150 }}>
                        <SearchableSelect
                            selected={filters.quality}
                            onChange={(v: string) => setQuality(v)}
                            options={QUALITY_FILTER_OPTIONS}
                            searchable={false}
                        />
                    </div>

                    {/* Styles toggle */}
                    <label style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        height: 38, padding: '0 12px',
                        border: `1px solid ${C.n200}`, borderRadius: 8,
                        background: C.w, fontSize: 13, color: C.n600,
                        cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
                    }}>
                        <input
                            type="checkbox"
                            checked={filters.style}
                            onChange={e => setStyle(e.target.checked)}
                            style={{ accentColor: C.p600, cursor: 'pointer', margin: 0 }}
                        />
                        With styles
                    </label>

                    {/* Reset — only when any filter is active */}
                    {hasActiveFilters && (
                        <button
                            onClick={resetFilters}
                            style={{
                                height: 38, padding: '0 12px',
                                border: `1px solid ${C.n200}`, borderRadius: 8,
                                background: C.w, fontSize: 13, color: C.n500,
                                cursor: 'pointer', whiteSpace: 'nowrap',
                            }}
                        >
                            Reset
                        </button>
                    )}
                </div>
            )}

            <VoicesBanner />

            {activeTab === 'explore' && !isLoading && !filtersChanged && <TrendingVoices />}

            {/* Active filter summary — shown only when filters ARE active (opposite of the
                TrendingVoices condition above) */}
            {activeTab === 'explore' && !isLoading && filtersChanged && (
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '9px 14px', marginBottom: 16,
                    borderRadius: 999,
                    background: C.p50, color: C.p700,
                    fontSize: 13, fontWeight: 600,
                }}>
                    <span style={{ color: C.p600 }}>
                        Showing {filteredCount} voice{filteredCount === 1 ? '' : 's'}
                    </span>
                    {searchQuery.trim() && (
                        <span style={{ color: C.p700, fontWeight: 500 }}>
                            · matching "{searchQuery.trim()}"
                        </span>
                    )}
                    {!searchQuery.trim() && selectedLanguage?.name && (
                        <span style={{ color: C.p700, fontWeight: 500 }}>
                            · {selectedLanguage.name}
                        </span>
                    )}
                </div>
            )}

            {/* ── Tab content ── */}
            {activeTab === 'explore' && (
                isLoading ? (
                    <AIVoicesSkeleton />
                ) : (
                    <>
                        <VoicesV2
                            key={selectedLanguage?.code ?? 'default'}
                            isPicker={false}
                            searchQuery={searchQuery}
                            onCountChange={handleVoicesCountChange}
                        />
                        {!filtersChanged && <LanguageCollections />}
                    </>
                )
            )}

            {activeTab === 'my-voices' && (
                <div ref={myVoicesRef}>
                    <MyVoices org_id={org_id} integration_id={id} searchQuery={searchQuery} />
                </div>
            )}

            {activeTab === 'favourites' && (
                <BookmarkedVoicesList org_id={org_id} onExplore={() => handleTabChange('explore')} />
            )}

            {/* Modals */}
            <SetDefaultVoiceModal />
            <CreateCustomProfileModal />
        </div>
    );
}
